import { useEffect, useId, useState } from 'react'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react'
import { BrowserRouter, Link, NavLink, Route, Routes, useMatch, useNavigate } from 'react-router-dom'
import { peticionDeAcceso } from './autenticacion'
import { leerClienteRecordado, recordarCliente } from './clienteActivo'
import {
  alAtenderSegundoFactor, alPedirSegundoFactor, api, cancelarSegundoFactor,
  Cliente, continuarConSegundoFactor, Despacho, ErrorDeApi, type Notificacion,
} from './servicios/api'
import {
  IconoBitacora,
  IconoCampana,
  IconoCatalogo,
  IconoCfdi,
  IconoClientes,
  IconoColaboradores,
  IconoContabilidadElectronica,
  IconoContabilizacion,
  IconoPolizas,
  IconoReportes,
  IconoTablero,
  LogoDeMicrosoft,
} from './iconos'
import { Tablero } from './paginas/Tablero'
import { Clientes } from './paginas/Clientes'
import { ContabilidadElectronica } from './paginas/ContabilidadElectronica'
import { RepositorioDelDespacho } from './paginas/RepositorioDelDespacho'
import { ExpedienteCliente } from './paginas/ExpedienteCliente'
import { CatalogoDeCuentas } from './paginas/CatalogoDeCuentas'
import { Polizas } from './paginas/Polizas'
import { PlantillasDePoliza } from './paginas/PlantillasDePoliza'
import { RepositorioCfdi } from './paginas/RepositorioCfdi'
import { PanelDeContabilizacion } from './paginas/PanelDeContabilizacion'
import { Reportes } from './paginas/Reportes'
import { ControlDeEnvios } from './paginas/ControlDeEnvios'
import { BalanzaAjustada } from './paginas/BalanzaAjustada'
import { PapelDeIva } from './paginas/PapelDeIva'
import { PapelDeIsr } from './paginas/PapelDeIsr'
import { Declaraciones } from './paginas/Declaraciones'
import { ConciliacionCfdi } from './paginas/ConciliacionCfdi'
import { SeriesYFolios } from './paginas/SeriesYFolios'
import { ConciliacionBancaria } from './paginas/ConciliacionBancaria'
import { Usuarios } from './paginas/Usuarios'
import { Bitacora } from './paginas/Bitacora'
import { AvisoDePrivacidad } from './paginas/AvisoDePrivacidad'
import { CatalogosDelSat } from './paginas/CatalogosDelSat'
import { ProveedorDeAvisos, useAvisos } from './componentes/Toast'
import { useTrampaDeFoco } from './ganchos/useTrampaDeFoco'
import { telon } from './ganchos/telon'
import { ProveedorDeAyuda } from './ayuda/ContextoDeAyuda'
import { MenuDeAyuda } from './ayuda/MenuDeAyuda'
import { Recorrido } from './ayuda/Recorrido'
import { TipDePantalla } from './ayuda/TipDePantalla'
import { Novedades } from './ayuda/Novedades'

/** Las seis secciones que trabajan sobre un cliente concreto. */
const SECCIONES_DE_CLIENTE = [
  { sufijo: 'catalogo', texto: 'Catálogo de cuentas', Icono: IconoCatalogo },
  { sufijo: 'polizas', texto: 'Pólizas y periodos', Icono: IconoPolizas },
  { sufijo: 'cfdi', texto: 'CFDI', Icono: IconoCfdi },
  { sufijo: 'contabilizacion', texto: 'Contabilización', Icono: IconoContabilizacion },
  { sufijo: 'reportes', texto: 'Reportes', Icono: IconoReportes },
  { sufijo: 'control-envios', texto: 'Control de envíos', Icono: IconoContabilidadElectronica },
]

function Portada() {
  const { instance } = useMsal()
  // L6 · `loginRedirect` se lleva la pestaña entera, y entre el clic y el salto la página se
  // queda igual: sin esto, quien no ve respuesta vuelve a pulsar y dispara un segundo redirect.
  const [redirigiendo, setRedirigiendo] = useState(false)

  return (
    <main className="portada">
      <div className="portada-tarjeta">
        <div className="logo">
          <span className="logo-cuadro" />
          <span className="logo-nombre">ContaQuick</span>
        </div>
        <div>
          <h1>Entrar al despacho</h1>
          <p className="lema">Contabilidad en la nube para contadores en México.</p>
        </div>
        {/* L3 · Decir «Iniciar sesión» esconde a dónde lleva el botón: el único método es Entra
            ID, y quien lo pulsa sale de ContaQuick a un dominio de Microsoft. Nombrarlo antes
            del salto evita que parezca que la sesión se perdió o que la página se equivocó. */}
        <button
          className="primario"
          disabled={redirigiendo}
          onClick={() => {
            setRedirigiendo(true)
            instance.loginRedirect(peticionDeAcceso).catch(() => setRedirigiendo(false))
          }}
        >
          <LogoDeMicrosoft />
          {redirigiendo ? 'Redirigiendo a Microsoft…' : 'Continuar con Microsoft Entra ID'}
        </button>
        <p className="nota">
          ¿Tu despacho aún no está de alta? Pide a ContaQuick que lo dé de alta con tu correo y
          después crea tu cuenta iniciando sesión con ese mismo correo.
        </p>
      </div>
    </main>
  )
}

/** Avisos en la aplicación: vencimientos, descargas, certificados y cancelaciones (RF-902, H-02). */
function CampanaDeNotificaciones() {
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([])
  const [abierta, setAbierta] = useState(false)
  const [cargando, setCargando] = useState(false)

  async function recargar() {
    try {
      setNotificaciones(await api.notificaciones(true))
    } catch {
      // Sin despacho todavía, o sesión sin permisos: la campana simplemente no muestra nada.
    }
  }

  useEffect(() => {
    void recargar()
  }, [])

  async function generar() {
    setCargando(true)
    try {
      await api.generarNotificaciones()
      await recargar()
    } catch {
      /* se reintenta la próxima vez que se abra la campana */
    } finally {
      setCargando(false)
    }
  }

  async function marcarLeida(id: string) {
    setNotificaciones((actual) => actual.filter((n) => n.id !== id))
    try {
      await api.marcarNotificacionLeida(id)
    } catch {
      void recargar()
    }
  }

  return (
    <span style={{ position: 'relative' }}>
      <button
        onClick={() => setAbierta((a) => !a)}
        title="Notificaciones"
        aria-label={
          notificaciones.length > 0
            ? `Notificaciones, ${notificaciones.length} sin leer`
            : 'Notificaciones'
        }
        aria-haspopup="true"
        aria-expanded={abierta}
      >
        <IconoCampana />
        {notificaciones.length > 0 && <strong>{notificaciones.length}</strong>}
      </button>

      {abierta && (
        <div
          className="panel"
          style={{ position: 'absolute', right: 0, top: '110%', width: 360, maxHeight: 420, overflow: 'auto', zIndex: 20 }}
        >
          <div className="acciones" style={{ marginTop: 0 }}>
            <strong>Notificaciones</strong>
            <button onClick={() => void generar()} disabled={cargando} style={{ marginLeft: 'auto' }}>
              {cargando ? 'Buscando…' : 'Buscar avisos nuevos'}
            </button>
          </div>

          {notificaciones.length === 0 ? (
            <p className="sutil">Sin avisos pendientes.</p>
          ) : (
            notificaciones.map((n) => (
              <div key={n.id} className="notificacion">
                <div className="texto">
                  <p style={{ margin: 0, fontWeight: 700 }}>{n.titulo}</p>
                  <p className="sutil" style={{ margin: '2px 0' }}>{n.mensaje}</p>
                  <button className="enlace" onClick={() => void marcarLeida(n.id)}>Marcar leída</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </span>
  )
}

/** Iniciales para el avatar: las de las dos primeras palabras del nombre. */
function iniciales(nombre: string): string {
  const partes = nombre.split(/[\s._-]+/).filter(Boolean)
  return partes.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}

const ETIQUETA_DE_ROL: Record<string, string> = {
  Administrador: 'Administrador del despacho',
  Contador: 'Contador',
  Auxiliar: 'Auxiliar',
  SoloLectura: 'Solo lectura',
}

/**
 * Un rol que la SPA no conozca —porque el servidor añadió uno nuevo, o porque la respuesta
 * llegó incompleta— no puede tumbar la aplicación: se muestra tal cual viene.
 */
function etiquetaDeRol(rol: string | undefined): string {
  if (!rol) return 'Sin rol'
  return ETIQUETA_DE_ROL[rol] ?? rol
}

/**
 * Selector de cliente activo.
 *
 * Seis de las nueve secciones trabajan sobre un cliente, y sus rutas llevan el id dentro
 * (/clientes/:id/cfdi). Eso está bien —hace que un enlace se pueda compartir y que el
 * aislamiento por inquilino se vea en la URL— pero deja la navegación lateral sin saber a qué
 * cliente apuntar. Este selector lo resuelve sin inventar un estado paralelo: **la URL manda**.
 * Si la ruta ya trae cliente, el selector lo refleja y elegir otro reescribe solo ese segmento;
 * si la ruta es global (tablero, bitácora), el selector solo decide a dónde apuntan las
 * secciones de cliente, sin navegar a ninguna parte.
 */
function useClienteActivo(clientes: Cliente[]) {
  const enModulo = useMatch('/clientes/:id/:seccion')
  const enExpediente = useMatch('/clientes/:id')
  const navegar = useNavigate()
  const idEnLaRuta = enModulo?.params.id ?? enExpediente?.params.id ?? ''
  const [recordado, setRecordado] = useState(leerClienteRecordado)

  useEffect(() => {
    if (idEnLaRuta) {
      setRecordado(idEnLaRuta)
      recordarCliente(idEnLaRuta)
    }
  }, [idEnLaRuta])

  // El recordado es una pista, no una verdad: si ese cliente ya no está en la cartera
  // (se archivó, o cambió el usuario), se cae al primero de la lista.
  const respaldo = clientes.some((c) => c.id === recordado) ? recordado : clientes[0]?.id ?? ''
  const clienteActivo = idEnLaRuta || respaldo

  function cambiar(id: string) {
    setRecordado(id)
    recordarCliente(id)
    if (enModulo) navegar(`/clientes/${id}/${enModulo.params.seccion}`)
    else if (enExpediente) navegar(`/clientes/${id}`)
    // En una ruta global no se navega: cambiar de cliente ahí solo reapunta la navegación.
  }

  return { clienteActivo, cambiar, idEnLaRuta }
}

function BarraLateral({
  despacho,
  clientes,
  totalDeClientes,
  clienteActivo,
  alCambiarCliente,
}: {
  despacho: Despacho
  clientes: Cliente[]
  totalDeClientes: number
  clienteActivo: string
  alCambiarCliente: (id: string) => void
}) {
  const { instance } = useMsal()
  const cuenta = instance.getActiveAccount()
  const correo = cuenta?.username ?? ''
  // `name` es opcional en MSAL y con Entra External ID no siempre viene; la parte anterior a
  // la arroba es el mejor respaldo que se tiene sin pedir la ficha del colaborador.
  const nombre = cuenta?.name || correo.split('@')[0] || 'Mi cuenta'
  const esAdministrador = despacho.rolActual === 'Administrador'
  const hayCliente = clienteActivo !== ''

  return (
    <aside className="lateral">
      <div className="lateral-marca">
        <span className="logo" />
        <span className="nombre">ContaQuick</span>
      </div>

      <div className="lateral-despacho">
        <div className="kicker">Despacho</div>
        <div className="nombre">{despacho.nombreComercial}</div>
      </div>

      <div className="lateral-cliente">
        <label className="kicker" htmlFor="cliente-activo">Cliente</label>
        {clientes.length === 0 ? (
          <p className="sutil" style={{ margin: 0 }}>
            Sin clientes todavía. <Link to="/clientes">Da de alta el primero →</Link>
          </p>
        ) : (
          <>
            <select
              id="cliente-activo"
              data-recorrido="selector-de-cliente"
              value={clienteActivo}
              onChange={(e) => alCambiarCliente(e.target.value)}
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.razonSocial}</option>
              ))}
            </select>
            {totalDeClientes > clientes.length && (
              <p className="sutil" style={{ margin: '4px 0 0' }}>
                Se muestran {clientes.length} de {totalDeClientes}. Búscalos todos en{' '}
                <Link to="/clientes">Clientes</Link>.
              </p>
            )}
          </>
        )}
      </div>

      <nav className="lateral-nav" aria-label="Secciones">
        <NavLink to="/" end><IconoTablero />Tablero</NavLink>
        <NavLink to="/clientes"><IconoClientes />Clientes</NavLink>
        {/* A1: transversal a propósito. El cierre mensual del Anexo 24 se sigue de todos los
            clientes a la vez, no de uno; la vista por cliente sigue viviendo en su expediente. */}
        <NavLink to="/contabilidad-electronica">
          <IconoContabilidadElectronica />Contabilidad electrónica
        </NavLink>
        {/* F6: los CFDI de toda la cartera. La vista por cliente sigue en su expediente. */}
        <NavLink to="/cfdi"><IconoCfdi />CFDI de la cartera</NavLink>

        {SECCIONES_DE_CLIENTE.map(({ sufijo, texto, Icono }) =>
          hayCliente ? (
            <NavLink key={sufijo} to={`/clientes/${clienteActivo}/${sufijo}`}>
              <Icono />{texto}
            </NavLink>
          ) : (
            // Sin cliente no hay ruta válida a la que apuntar: un enlace roto sería peor que
            // una entrada apagada que dice por qué no se puede entrar.
            <span key={sufijo} className="nav-inerte" aria-disabled="true" title="Elige un cliente para entrar">
              <Icono />{texto}
            </span>
          ),
        )}

        {esAdministrador && (
          <>
            <NavLink to="/usuarios"><IconoColaboradores />Colaboradores</NavLink>
            <NavLink to="/bitacora"><IconoBitacora />Bitácora</NavLink>
          </>
        )}
      </nav>

      <div className="lateral-pie">
        <div className="usuario-bloque">
          <span className="usuario-avatar" aria-hidden="true">{iniciales(nombre)}</span>
          <div style={{ minWidth: 0 }}>
            <div className="nombre" title={correo}>{nombre}</div>
            <div className="rol">{etiquetaDeRol(despacho.rolActual)}</div>
          </div>
        </div>
        <Link to="/aviso-de-privacidad" className="sutil">Aviso de privacidad</Link>
        <button className="enlace" onClick={() => instance.logoutRedirect()}>Salir</button>
      </div>
    </aside>
  )
}

/** Barra mínima para los estados en que todavía no se sabe si hay despacho: solo marca y salida. */
function ArmazonMinimo({ children }: { children: React.ReactNode }) {
  const { instance } = useMsal()

  return (
    <>
      <nav className="barra">
        <span className="marca">ContaQuick</span>
        <span className="espaciador" />
        <span className="usuario">{instance.getActiveAccount()?.username}</span>
        <button onClick={() => instance.logoutRedirect()}>Salir</button>
      </nav>
      <main id="contenido-principal" className="contenido">{children}</main>
    </>
  )
}

/**
 * V2 · Las dos pantallas de administración —colaboradores y bitácora— son solo del
 * administrador del despacho. Los enlaces ya se le ocultaban a los demás roles en la barra
 * lateral, pero la dirección se puede teclear: quien llegaba así entraba a la pantalla y
 * descubría la restricción por el «Error 403» crudo del API. Esto lo dice en su idioma y
 * antes de pedir nada.
 *
 * La ruta se queda montada con este mensaje en vez de desmontarse: la SPA no tiene ruta
 * comodín, así que quitarla dejaría el área de trabajo en blanco, que es peor que el 403.
 *
 * Es una guarda de presentación, no de seguridad: quien autoriza sigue siendo el servidor.
 * Nadie debe aflojar un chequeo del backend apoyándose en esta pantalla.
 */
function SinAccesoPorRol({ seccion, rol }: { seccion: string; rol: string | undefined }) {
  return (
    <div className="tarjeta" style={{ maxWidth: 560, margin: 'var(--space-6) auto' }} role="alert">
      <h1 style={{ all: 'unset', fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 17 }}>
        Tu rol no tiene acceso a {seccion}
      </h1>
      <p className="sutil" style={{ margin: '8px 0 0' }}>
        Tu rol en este despacho: <strong>{etiquetaDeRol(rol)}</strong>. La bitácora y la
        administración de colaboradores solo las abre el administrador del despacho, así que
        esta pantalla no se muestra aunque se escriba su dirección. Si necesitas consultarla,
        pide a un administrador del despacho que te cambie el rol.
      </p>
      <div className="acciones">
        <Link className="boton" to="/">Ir al tablero</Link>
      </div>
    </div>
  )
}

/**
 * Compuerta del área autenticada (RF-101, RF-102, RF-107): nada del despacho se muestra
 * hasta confirmar que la cuenta pertenece a uno. El alta de despachos ya no es autoservicio
 * (RF-107): si el correo no pertenece a ninguno, solo queda pedirle a ContaQuick que lo dé de
 * alta desde la consola de plataforma.
 *
 * El despacho que ya se pedía aquí para saber si existe ahora además se conserva: alimenta el
 * nombre y el rol de la barra lateral sin una segunda petición.
 */
function PuertaDeDespacho({ children }: { children: (despacho: Despacho) => React.ReactNode }) {
  const { instance } = useMsal()
  const cuenta = instance.getActiveAccount()
  const [despacho, setDespacho] = useState<Despacho | null>(null)
  const [estado, setEstado] = useState<'cargando' | 'ok' | 'sinDespacho' | 'error'>('cargando')
  const [mensaje, setMensaje] = useState('')

  async function comprobar() {
    setEstado('cargando')
    try {
      setDespacho(await api.despachoActual())
      setEstado('ok')
    } catch (e) {
      if (e instanceof ErrorDeApi && (e.codigo === 'SIN_DESPACHO' || (e.estado === 403 && e.message.includes('despacho')))) {
        setEstado('sinDespacho')
      } else {
        setMensaje(e instanceof Error ? e.message : 'No se pudo comprobar tu despacho.')
        setEstado('error')
      }
    }
  }

  useEffect(() => {
    void comprobar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (estado === 'ok' && despacho) return <>{children(despacho)}</>

  if (estado === 'cargando') {
    return (
      <ArmazonMinimo>
        <p className="vacio" aria-live="polite">Comprobando tu despacho…</p>
      </ArmazonMinimo>
    )
  }

  if (estado === 'error') {
    return (
      <ArmazonMinimo>
        <p className="error" role="alert">{mensaje}</p>
        <button onClick={() => void comprobar()}>Reintentar</button>
      </ArmazonMinimo>
    )
  }

  return (
    <ArmazonMinimo>
      <div className="tarjeta" style={{ maxWidth: 520, margin: '10vh auto' }}>
        <h1 style={{ all: 'unset', fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: 17 }}>
          Tu cuenta aún no tiene despacho
        </h1>
        <p className="sutil" style={{ margin: '8px 0 0' }}>
          Iniciaste sesión como <strong>{cuenta?.username}</strong>, pero ese correo no pertenece a
          ningún despacho dado de alta. Si tu despacho es nuevo, pide a ContaQuick que lo dé de
          alta con este correo; si ya existe, pide a tu administrador que te invite como
          colaborador.
        </p>
        <div className="acciones">
          <button className="primario" onClick={() => void comprobar()}>Volver a comprobar</button>
        </div>
      </div>
    </ArmazonMinimo>
  )
}

function AreaDeTrabajo({ despacho }: { despacho: Despacho }) {
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [totalDeClientes, setTotalDeClientes] = useState(0)
  const { clienteActivo, cambiar, idEnLaRuta } = useClienteActivo(clientes)

  useEffect(() => {
    async function cargar() {
      try {
        // 200 es el techo del API. Un despacho del plan más grande puede tener 250 clientes;
        // en ese caso la barra lo dice y manda a la pantalla de Clientes, que sí busca.
        const pagina = await api.listarClientes({ estado: 'Activo', tamano: 200 })
        setClientes(pagina.items)
        setTotalDeClientes(pagina.total)
      } catch {
        // La cartera no es imprescindible para navegar: sin ella el selector queda vacío
        // y las secciones de cliente apagadas, pero el tablero y la bitácora funcionan.
      }
    }
    void cargar()
  }, [])

  const esAdministrador = despacho.rolActual === 'Administrador'

  return (
    // RF-1700 · La ayuda conoce el rol para no enseñar acciones que este usuario no puede hacer (RN-65).
    <ProveedorDeAyuda rol={despacho.rolActual}>
      <div className="armazon">
        <BarraLateral
          despacho={despacho}
          clientes={clientes}
          totalDeClientes={totalDeClientes}
          clienteActivo={clienteActivo}
          alCambiarCliente={cambiar}
        />

        <div style={{ minWidth: 0 }}>
          {!esAdministrador && (
            <p className="banda-rol" style={{ margin: 0 }}>
              Vista de {etiquetaDeRol(despacho.rolActual).toLowerCase()}: {totalDeClientes}{' '}
              {totalDeClientes === 1 ? 'cliente asignado' : 'clientes asignados'}. La bitácora y la
              administración de colaboradores quedan fuera de este rol.
            </p>
          )}

          <div className="encabezado" style={{ justifyContent: 'flex-end', padding: '12px 24px 0' }}>
            <MenuDeAyuda />
            <CampanaDeNotificaciones />
          </div>

          <main id="contenido-principal" className="contenido">
            {/*
              React Router no vuelve a montar la página cuando solo cambia :id, así que el estado
              local sobrevive al cambio de cliente: la selección de CFDI del cliente anterior
              seguiría marcada sobre la tabla del nuevo, y un modal abierto se confirmaría sobre
              quien no era. La clave fuerza el remonte y con él una pizarra limpia.

              Va sobre el id que trae la RUTA, no sobre el cliente activo: el activo empieza vacío
              y se rellena cuando llega la cartera, así que usarlo remontaría —y volvería a pedir
              datos— en cada primera carga. En las rutas globales la clave es constante.
            */}
            <Routes key={idEnLaRuta}>
              <Route path="/" element={<Tablero />} />
              <Route path="/clientes" element={<Clientes />} />
              {/* V2 · Las dos rutas de administración existen para cualquiera —si no, el área de
                  trabajo quedaría en blanco—, pero solo el administrador ve la pantalla. */}
              <Route
                path="/usuarios"
                element={
                  esAdministrador
                    ? <Usuarios />
                    : <SinAccesoPorRol seccion="Colaboradores" rol={despacho.rolActual} />
                }
              />
              <Route
                path="/bitacora"
                element={
                  esAdministrador
                    ? <Bitacora />
                    : <SinAccesoPorRol seccion="Bitácora" rol={despacho.rolActual} />
                }
              />
              <Route path="/contabilidad-electronica" element={<ContabilidadElectronica />} />
              <Route path="/cfdi" element={<RepositorioDelDespacho />} />
              <Route path="/aviso-de-privacidad" element={<AvisoDePrivacidad />} />
              <Route path="/catalogos" element={<CatalogosDelSat />} />
              <Route path="/clientes/:id" element={<ExpedienteCliente />} />
              <Route path="/clientes/:id/catalogo" element={<CatalogoDeCuentas />} />
              <Route path="/clientes/:id/polizas" element={<Polizas />} />
              <Route path="/clientes/:id/plantillas-poliza" element={<PlantillasDePoliza />} />
              <Route path="/clientes/:id/cfdi" element={<RepositorioCfdi />} />
              <Route path="/clientes/:id/contabilizacion" element={<PanelDeContabilizacion />} />
              <Route path="/clientes/:id/reportes" element={<Reportes />} />
              <Route path="/clientes/:id/control-envios" element={<ControlDeEnvios />} />
              <Route path="/clientes/:id/balanza-ajustada" element={<BalanzaAjustada />} />
              <Route path="/clientes/:id/iva" element={<PapelDeIva />} />
              <Route path="/clientes/:id/isr" element={<PapelDeIsr />} />
              <Route path="/clientes/:id/declaraciones" element={<Declaraciones />} />
              <Route path="/clientes/:id/conciliacion-cfdi" element={<ConciliacionCfdi />} />
              <Route path="/clientes/:id/folios" element={<SeriesYFolios />} />
              <Route path="/clientes/:id/conciliacion-bancaria" element={<ConciliacionBancaria />} />
            </Routes>
          </main>
        </div>
      </div>
      <Recorrido />
      <TipDePantalla />
      <Novedades />
    </ProveedorDeAyuda>
  )
}

/**
 * M2 · El segundo factor se pide con un redirect que se lleva la pestaña entera: el usuario
 * sale a Microsoft, vuelve, y la aplicación sigue como si nada hubiera pasado. Sin un acuse,
 * ese viaje se lee como un parpadeo raro de la sesión.
 *
 * Va como toast y no como aviso fijo porque cumple la regla de `Toast.tsx`: es algo que YA
 * ocurrió y salió bien, y perdérselo no cuesta nada —el resultado es que la pantalla que
 * pediste ya está cargada—.
 */
function AcuseDeSegundoFactor() {
  const avisar = useAvisos()
  // M3 · El otro extremo del mismo viaje: antes de salir a Microsoft, decir por qué.
  const [pidiendo, setPidiendo] = useState(false)

  useEffect(() => {
    alAtenderSegundoFactor(() => avisar('Verificación en dos pasos completada. Ya puedes seguir.'))
    alPedirSegundoFactor(() => setPidiendo(true))
    return () => {
      alAtenderSegundoFactor(null)
      alPedirSegundoFactor(null)
    }
  }, [avisar])

  if (!pidiendo) return null

  return (
    <DialogoDeSegundoFactor
      onContinuar={() => void continuarConSegundoFactor()}
      onCerrar={() => {
        setPidiendo(false)
        cancelarSegundoFactor()
      }}
    />
  )
}

/**
 * M3 · Lo que la aplicación tiene que decir antes de llevarse al usuario a otro dominio.
 *
 * El diálogo SÍ se puede cerrar, aunque cerrarlo deje la aplicación sin poder cargar nada. Un
 * modal del que no se sale es una trampa —para el teclado y para quien solo quería terminar de
 * leer la pantalla—, y la salida honesta es decir qué pasa si no continúas, no bloquear la
 * puerta. Cerrarlo suelta el guard, así que la siguiente petición vuelve a preguntar.
 */
function DialogoDeSegundoFactor({ onContinuar, onCerrar }: {
  onContinuar: () => void
  onCerrar: () => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, onCerrar)

  return (
    <div className="modal" {...telon(onCerrar)}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo}>Tu rol requiere verificación en dos pasos</h2>
        <p>
          Para lo que estás haciendo, ContaQuick necesita confirmar que eres tú. Al continuar te
          llevamos a Microsoft, que es donde vive tu cuenta, y vuelves aquí en cuanto termines.
        </p>
        <p className="nota">
          Si no continúas, la aplicación no podrá cargar datos: la verificación es lo que falta,
          no un paso opcional. Puedes volver a intentarlo cuando quieras.
        </p>
        <div className="acciones">
          <button onClick={onCerrar}>Ahora no</button>
          <button className="primario" onClick={onContinuar}>
            <LogoDeMicrosoft />
            Continuar
          </button>
        </div>
      </div>
    </div>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <ProveedorDeAvisos>
        <AcuseDeSegundoFactor />

        <UnauthenticatedTemplate>
          <Portada />
        </UnauthenticatedTemplate>

        <AuthenticatedTemplate>
          <a href="#contenido-principal" className="saltar-contenido">Saltar al contenido</a>
          <PuertaDeDespacho>{(despacho) => <AreaDeTrabajo despacho={despacho} />}</PuertaDeDespacho>
        </AuthenticatedTemplate>
      </ProveedorDeAvisos>
    </BrowserRouter>
  )
}
